/**
 * Getting a picture of a model, from wherever one can be had.
 *
 * Three places, in order of what they cost. One already made and held in this page; one
 * stored from a previous visit; and failing both, one taken now — which means reading the
 * file back, building its geometry, photographing it and throwing the geometry away again.
 *
 * ## Why they are taken late rather than at upload
 *
 * "Pre-rendered" is the point of storing them, but taking them at upload would charge the
 * whole bill at the worst moment. Dropping twenty G-code files already rebuilds twenty
 * printed objects to measure them; doing it a second time each, to take a picture nobody has
 * asked to see, would double the slowest thing this application does while somebody watches
 * a progress-free file input. So folding the sidebar away is what sets them being taken, one
 * model at a time, and the answers are stored — paid once ever, in the background, by
 * somebody who is looking at squares rather than at models, and never again afterwards.
 *
 * Hence `setActive`. The strip is built whether or not it is showing, since it is a
 * `display: none` away from being on screen either way, so something has to say that nobody
 * is looking at it — otherwise every visit would quietly rebuild the geometry of every model
 * in the library on behalf of a panel the user may never open.
 *
 * ## One at a time
 *
 * Strictly serial, because every one of these borrows the application's renderer and builds
 * geometry on the main thread. Twenty at once would be twenty geometry builds racing each
 * other through one thread, which is not faster than twenty in a row and does visibly stop
 * the page. In a queue, each finishes and hands its square a picture while the rest wait.
 */

import { colourOf } from '../domain/colour.js';
import type { ModelId, ModelMeta } from '../domain/types.js';
import { logger } from '../log.js';
import type { ModelStore } from '../storage/modelStore.js';
import type { SceneManager } from '../viewer/sceneManager.js';
import { ThumbnailMaker } from '../viewer/thumbnail.js';

const log = logger('models');

export interface ThumbnailsOptions {
  readonly scene: SceneManager;
  readonly store: ModelStore;
  /** A picture has arrived for a model that had none. Redraw whatever is showing it. */
  readonly onReady: (id: ModelId) => void;
}

export class Thumbnails {
  private readonly maker: ThumbnailMaker;

  /**
   * Object URLs by model, and the colour each was drawn in.
   *
   * Held rather than made afresh on every render: the sidebar is rebuilt wholesale
   * whenever anything about the library changes, and a new URL per square per render would
   * leak one for every render that ever happened.
   */
  private readonly urls = new Map<ModelId, { colour: string; url: string }>();

  /** Models already asked about, so a rebuild of the list does not re-queue everything. */
  private readonly asked = new Set<string>();

  private queue: Promise<void> = Promise.resolve();

  /** Whether anybody is looking at the squares. Nothing is photographed while nobody is. */
  private active = false;

  constructor(private readonly options: ThumbnailsOptions) {
    this.maker = new ThumbnailMaker(options.scene.renderer);
  }

  /**
   * Say whether the strip of squares is on screen.
   *
   * Pictures already made go on being handed out either way — they cost nothing and the
   * squares are drawn regardless. What this gates is the making of new ones.
   */
  setActive(active: boolean): void {
    this.active = active;
  }

  /**
   * The picture of a model if there is one to hand, and an errand to fetch one if not.
   *
   * Synchronous on purpose. It is called while building the sidebar, which must not wait on
   * storage or on a renderer to put a square on the page — so it answers with what it has
   * and reports through `onReady` when it has more.
   */
  urlFor(meta: ModelMeta): string | undefined {
    const wanted = colourOf(meta);
    const held = this.urls.get(meta.id);
    if (held?.colour === wanted) return held.url;

    this.request(meta, wanted);
    return undefined;
  }

  /**
   * Forget a model's picture, because the model has gone.
   *
   * The stored copy goes with the record, in the same transaction; this is the copy held in
   * the page, whose object URL keeps the image alive in memory until it is let go of.
   */
  forget(id: ModelId): void {
    const held = this.urls.get(id);
    if (!held) return;

    URL.revokeObjectURL(held.url);
    this.urls.delete(id);

    for (const key of [...this.asked]) {
      if (key.startsWith(`${id}:`)) this.asked.delete(key);
    }
  }

  dispose(): void {
    for (const id of [...this.urls.keys()]) this.forget(id);
    this.maker.dispose();
  }

  /** Join the queue, unless nobody is looking or this exact question is already in it. */
  private request(meta: ModelMeta, colour: string): void {
    if (!this.active) return;

    const key = `${meta.id}:${colour}`;
    if (this.asked.has(key)) return;
    this.asked.add(key);

    this.queue = this.queue
      .then(async () => {
        await this.fetch(meta, colour);
      })
      .catch((error: unknown) => {
        // One model that will not be photographed is one square without a picture, which
        // is a state the sidebar already draws. It must not stop the queue behind it.
        this.asked.delete(key);
        log.warn(`No thumbnail could be made for ${meta.name}.`, error);
      });
  }

  private async fetch(meta: ModelMeta, colour: string): Promise<void> {
    const stored = await this.options.store.getThumbnail(meta.id);
    const image =
      stored?.colour === colour ? stored.image : await this.take(meta, colour);
    if (!image) return;

    this.adopt(meta.id, colour, image);
    this.options.onReady(meta.id);
  }

  /**
   * Photograph a model, loading it if it is not already in the scene.
   *
   * A model already loaded is left loaded — it is the one on display, or one standing in
   * the room, and unloading it would take it off the screen. One loaded only for this is
   * unloaded again, since a library of thirty models is not a library of thirty geometries
   * anybody asked to keep in memory.
   */
  private async take(meta: ModelMeta, colour: string): Promise<Blob | undefined> {
    const { scene, store } = this.options;

    const resident = scene.get(meta.id);
    let model = resident;

    if (!model) {
      const file = await store.getFile(meta.id);
      if (!file) return undefined;
      model = await scene.load(meta.id, await file.arrayBuffer(), meta, colour);
    }

    try {
      const image = await this.maker.capture(model);
      if (image) await store.putThumbnail(meta.id, { colour, image });
      return image;
    } finally {
      if (!resident) scene.unload(meta.id);
    }
  }

  private adopt(id: ModelId, colour: string, image: Blob): void {
    const previous = this.urls.get(id);
    if (previous) URL.revokeObjectURL(previous.url);

    this.urls.set(id, { colour, url: URL.createObjectURL(image) });
  }
}
